// @ts-nocheck
import { EXPLOSION_BURN } from "../../data";
import { TAU, clamp, lerp, rand, rgba } from "../../util";
import { Particle } from "../../entities";
import { Game } from "../game-base";

function burnRadius(cfg, blastR) {
  if (blastR != null && blastR > 0) return Math.max(14, blastR * (cfg.rMul ?? 1));
  return cfg.r ?? 60;
}

function spikeHash(seed, i) {
  const h = Math.sin(seed * 127.1 + i * 311.7) * 43758.5453;
  return h - Math.floor(h);
}

/** Full radial splatter — explosions on open ground. */
function makeBurnSpikes(seed, count) {
  const spikes = [];
  const rot = spikeHash(seed, 0) * TAU;
  for (let i = 0; i < count; i++) {
    const t = i / count;
    const a = rot + t * TAU + (spikeHash(seed, i + 1) - 0.5) * 0.55;
    spikes.push({
      a,
      reach: 0.28 + spikeHash(seed, i + 40) * 0.82,
      width: 0.035 + spikeHash(seed, i + 80) * 0.11,
      base: 0.04 + spikeHash(seed, i + 120) * 0.14,
      kink: (spikeHash(seed, i + 160) - 0.5) * 0.22,
    });
  }
  return spikes;
}

/** Directional fan — obstacle hits; angles are local (+x = fan forward). */
function makeFanBurnSpikes(seed, count, halfArc) {
  const spikes = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) : 0.5;
    const a = (t - 0.5) * 2 * halfArc + (spikeHash(seed, i + 1) - 0.5) * 0.18;
    const edge = Math.abs(t - 0.5) * 2;
    spikes.push({
      a,
      reach: 0.22 + spikeHash(seed, i + 40) * (0.55 + edge * 0.35),
      width: 0.03 + spikeHash(seed, i + 80) * 0.09,
      base: 0.03 + spikeHash(seed, i + 120) * 0.1,
      kink: (spikeHash(seed, i + 160) - 0.5) * 0.16,
    });
  }
  return spikes;
}

function burnSpreadEase(t, dur) {
  const p = clamp(t / Math.max(0.02, dur), 0, 1);
  return 1 - Math.pow(1 - p, 6);
}

function paintSplatter(c, r, spread, alpha, spikes, opts) {
  const scale = r * spread;
  if (scale < 2 || alpha <= 0.001) return;
  const stretch = opts.stretch ?? 1;
  const inner = opts.inner ?? 0.12;
  const tipSoft = opts.tipSoft ?? 0.72;

  c.beginPath();
  for (const s of spikes) {
    const spLen = scale * s.reach;
    const w = scale * s.width * (opts.wide ?? 1);
    const ca = Math.cos(s.a), sa = Math.sin(s.a);
    const k = s.kink * scale;
    const mx = ca * spLen * 0.55 + Math.cos(s.a + 1.2) * k;
    const my = (sa * spLen * 0.55 + Math.sin(s.a + 1.2) * k) * stretch;
    const px = ca * spLen, py = sa * spLen * stretch;
    const tx = -sa * w, ty = ca * w * stretch;
    const bx = ca * scale * inner * s.base;
    const by = sa * scale * inner * s.base * stretch;
    const tipW = w * tipSoft;
    c.moveTo(bx - tx, by - ty);
    c.lineTo(px - tx * 0.35, py - ty * 0.35);
    c.quadraticCurveTo(mx, my, px + tipW * ca, py + tipW * sa);
    c.lineTo(px - tipW * ca, py - tipW * sa);
    c.quadraticCurveTo(mx, my, px + tx * 0.35, py + ty * 0.35);
    c.closePath();
  }
  c.fill();
}

function paintSplatterCore(c, r, spread, alpha, seed, spikes, opts) {
  const scale = r * spread * (opts?.tightCore ? 0.32 : 0.42);
  if (scale < 1.5 || alpha <= 0.001) return;
  c.beginPath();
  const n = 5 + Math.floor(spikes.length * (opts?.tightCore ? 0.25 : 0.35));
  const ox = opts?.tightCore ? scale * 0.08 : 0;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + seed * 0.17;
    const jit = 0.55 + spikeHash(seed, i + 200) * 0.55;
    const rr = scale * jit * (0.75 + spikeHash(seed, i + 240) * 0.45);
    const x = ox + Math.cos(a) * rr;
    const y = Math.sin(a) * rr * 0.78;
    c.moveTo(x + 2, y);
    c.arc(x, y, 1.5 + spikeHash(seed, i + 280) * scale * 0.12, 0, TAU);
  }
  c.fill();
}

const FLOOR_BURN_MAX = 36;

function spawnBurnDust(game, x, y, r, cfg, fanA, fanArc) {
  // Keep burst dust light — each explosion used to spawn ~23 smoke particles and
  // drove GC pauses during heavy spell spam (see debug session spikes w/ 100+ particles).
  const dustN = Math.floor(2 + (cfg.dust ?? 0.6) * 7);
  const fan = fanA != null && Number.isFinite(fanA);
  const halfArc = fanArc ?? 1.05;
  for (let i = 0; i < dustN; i++) {
    const a = fan
      ? fanA + (rand(0, 1) - 0.5) * 2 * halfArc
      : rand(0, TAU);
    const sp = rand(90, 220) * (0.55 + r / 100);
    const ox = fan ? rand(0, r * 0.06) : rand(0, r * 0.05);
    game.particles.push(new Particle(
      x + Math.cos(a) * ox, y + Math.sin(a) * ox * 0.85,
      Math.cos(a) * sp, Math.sin(a) * sp,
      rand(0.22, 0.55), Math.random() < 0.55 ? "#2a1810" : "#4a3428",
      rand(5, 12) * clamp(r / 80, 0.6, 1.4), { kind: "smoke", grav: 0, drag: 0.78 }));
  }
  const settleN = Math.floor(dustN * 0.4);
  for (let i = 0; i < settleN; i++) {
    const a = fan
      ? fanA + (rand(0, 1) - 0.5) * 2 * halfArc
      : rand(0, TAU);
    const dist = rand(r * 0.08, r * 0.42);
    game.particles.push(new Particle(
      x + Math.cos(a) * dist, y + Math.sin(a) * dist * 0.75,
      Math.cos(a) * rand(20, 70), Math.sin(a) * rand(20, 70),
      rand(0.35, 0.75), "#1a100c",
      rand(7, 16) * clamp(r / 90, 0.55, 1.3), { kind: "smoke", grav: 0, drag: 0.88 }));
  }
}

Game.prototype.spawnFloorBurn = function (kind, x, y, blastR, opts) {
  opts = opts || {};
  const cfg = EXPLOSION_BURN[kind] || EXPLOSION_BURN.projectile;
  const r = burnRadius(cfg, blastR);
  const seed = Math.random() * 1000;
  const fan = opts.fanA != null && Number.isFinite(opts.fanA);
  const fanArc = opts.fanArc ?? cfg.fanArc ?? 1.05;
  const spikeN = fan ? 10 + Math.floor(r / 12) : 14 + Math.floor(r / 10);
  if (!this.floorBurns) this.floorBurns = [];
  if (this.floorBurns.length >= FLOOR_BURN_MAX) this.floorBurns.shift();
  this.floorBurns.push({
    x, y, r, kind,
    t: 0,
    life: cfg.life ?? 24,
    spreadDur: cfg.spreadDur ?? 0.1,
    intensity: cfg.intensity ?? 0.5,
    dust: cfg.dust ?? 0.6,
    seed,
    fanA: fan ? opts.fanA : null,
    fanArc,
    spikes: fan
      ? makeFanBurnSpikes(seed, spikeN, fanArc)
      : makeBurnSpikes(seed, spikeN),
    rot: fan ? 0 : (opts.rot != null && Number.isFinite(opts.rot) ? opts.rot : spikeHash(seed, 99) * TAU * 0.25),
  });
  spawnBurnDust(this, x, y, r, cfg, fan ? opts.fanA : null, fanArc);
};

Game.prototype.updateFloorBurns = function (dt) {
  if (!this.floorBurns?.length) return;
  for (const b of this.floorBurns) b.t += dt;
  this.floorBurns = this.floorBurns.filter(b => b.t < b.life);
};

Game.prototype.drawFloorBurns = function () {
  const c = this.ctx;
  if (!this.floorBurns?.length) return;
  const lite = this.floorBurns.length > 10;
  for (const b of this.floorBurns) {
    const cfg = EXPLOSION_BURN[b.kind] || EXPLOSION_BURN.projectile;
    const lifeP = clamp(b.t / b.life, 0, 1);
    const settleFade = 1 - clamp((lifeP - 0.72) / 0.28, 0, 1);
    const spread = burnSpreadEase(b.t, b.spreadDur);
    const alpha = (cfg.intensity ?? b.intensity ?? 0.5) * settleFade;
    const fan = b.fanA != null && Number.isFinite(b.fanA);
    const spikes = b.spikes || (fan ? makeFanBurnSpikes(b.seed, 16, b.fanArc) : makeBurnSpikes(b.seed, 16));

    c.save();
    c.translate(b.x, b.y);
    c.rotate(fan ? b.fanA : (b.rot || 0));

    c.globalCompositeOperation = "source-over";
    if (lite) {
      c.globalAlpha = alpha * lerp(0.5, 0.32, spread) * (1 - lifeP * 0.15);
      c.fillStyle = rgba("#2a1810", 0.72);
      paintSplatter(c, b.r, spread * 0.96, alpha, spikes, {
        wide: fan ? 0.95 : 1.05, inner: fan ? 0.08 : 0.1, tipSoft: 0.55, stretch: 0.8,
      });
      c.globalAlpha = alpha * 0.55;
      c.fillStyle = "#080604";
      paintSplatterCore(c, b.r, spread, alpha, b.seed, spikes, { tightCore: fan });
    } else {
      c.globalAlpha = alpha * lerp(0.55, 0.28, spread) * (1 - lifeP * 0.12);
      c.fillStyle = rgba("#5a4838", 0.55);
      paintSplatter(c, b.r, spread * 1.05, alpha, spikes, {
        wide: fan ? 1.35 : 1.55, inner: fan ? 0.06 : 0.08, tipSoft: 0.35, stretch: 0.82,
      });

      c.globalCompositeOperation = "multiply";
      c.globalAlpha = alpha * 0.62;
      c.fillStyle = "#2a1810";
      paintSplatter(c, b.r, spread * 0.96, alpha, spikes, {
        wide: fan ? 0.95 : 1.05, inner: fan ? 0.08 : 0.1, tipSoft: 0.55, stretch: 0.8,
      });

      c.globalAlpha = alpha * 0.78;
      c.fillStyle = "#120c0a";
      paintSplatter(c, b.r, spread * 0.82, alpha, spikes, {
        wide: fan ? 0.75 : 0.82, inner: fan ? 0.1 : 0.14, tipSoft: 0.65, stretch: 0.78,
      });

      c.globalAlpha = alpha * 0.88;
      c.fillStyle = "#080604";
      paintSplatterCore(c, b.r, spread, alpha, b.seed, spikes, { tightCore: fan });

      c.globalCompositeOperation = "source-over";
      c.globalAlpha = alpha * 0.42 * (1 - lifeP * 0.2);
      c.strokeStyle = rgba("#1a100c", 0.75);
      c.lineCap = "round";
      for (let i = 0; i < spikes.length; i += 2) {
        const s = spikes[i];
        const spLen = b.r * spread * s.reach;
        const w = 0.8 + spikeHash(b.seed, i + 300) * 2.2;
        c.lineWidth = w * clamp(b.r / 90, 0.65, 1.3);
        const ca = Math.cos(s.a), sa = Math.sin(s.a);
        const bx = ca * b.r * spread * (fan ? 0.04 : 0.08);
        const by = sa * b.r * spread * (fan ? 0.04 : 0.08) * 0.78;
        c.beginPath();
        c.moveTo(bx, by);
        c.lineTo(ca * spLen, sa * spLen * 0.78);
        c.stroke();
      }
    }

    c.restore();
  }
  c.globalAlpha = 1;
  c.globalCompositeOperation = "source-over";
};

// Legacy name used by spell previews.
Game.prototype.drawMeteorCraters = Game.prototype.drawFloorBurns;
